import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Graduacion } from '../../../models/graduacion.model';
import { CinturonColorUtil, CinturonColor, BeltColorInfo } from '../../../models/cinturon-colors';

@Component({
  selector: 'app-cinturon-graphics',
  templateUrl: './cinturon-graphics.component.html',
  styleUrls: ['./cinturon-graphics.component.css']
})
export class CinturonGraphicsComponent implements OnInit, OnChanges {
  @Input() graduacion: Graduacion | null = null;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() isLoading: boolean = false;

  beltColorInfo: BeltColorInfo = { isPunta: false, mainColor: CinturonColor.LOADING };

  ngOnInit(): void {
    this.updateBeltColorInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.graduacion || changes.isLoading) {
      this.updateBeltColorInfo();
    }
  }

  private updateBeltColorInfo(): void {
    if (this.isLoading) {
      this.beltColorInfo = { isPunta: false, mainColor: CinturonColor.LOADING };
      return;
    }
    
    if (!this.graduacion) {
      this.beltColorInfo = { isPunta: false, mainColor: CinturonColor.DEFAULT };
      return;
    }
    
    this.beltColorInfo = CinturonColorUtil.getColorInfoFromNombre(this.graduacion.nombre);
  }

  getCinturonStyle(): any {
    const colorInfo = this.beltColorInfo;
    
    if (colorInfo.isPunta && colorInfo.secondaryColor) {
      // Create a more precise gradient with hard stops to prevent color bleeding
      return {
        'background': `linear-gradient(to right, 
                      ${colorInfo.mainColor} 0%, 
                      ${colorInfo.mainColor} 60%, 
                      ${colorInfo.secondaryColor} 60%, 
                      ${colorInfo.secondaryColor} 100%)`
      };
    } else if (colorInfo.mainColor === CinturonColor.NEGRO) {
      // Special styling for black belts based on Dan level
      return this.getBlackBeltStyle();
    } else {
      // Single color for all other belts
      return {
        'background-color': colorInfo.mainColor
      };
    }
  }

  private getBlackBeltStyle(): any {
    if (!this.graduacion) {
      return { 'background-color': CinturonColor.NEGRO };
    }

    const graduacionId = this.graduacion.id;
    
    // Updated IDs to match new database schema
    if (graduacionId === 11) {
      // I DAN - single line
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%, 
                      ${CinturonColor.NEGRO} 88%, 
                      ${CinturonColor.AMARILLO} 88%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId === 12) {
      // II DAN - two lines
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%, 
                      ${CinturonColor.NEGRO} 81%, 
                      ${CinturonColor.AMARILLO} 81%, 
                      ${CinturonColor.AMARILLO} 85%,
                      ${CinturonColor.NEGRO} 85%, 
                      ${CinturonColor.NEGRO} 88%, 
                      ${CinturonColor.AMARILLO} 88%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId === 13) {
      // III DAN - three lines
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%,
                      ${CinturonColor.NEGRO} 75%,
                      ${CinturonColor.AMARILLO} 75%, 
                      ${CinturonColor.AMARILLO} 78%,
                      ${CinturonColor.NEGRO} 78%,
                      ${CinturonColor.NEGRO} 81%, 
                      ${CinturonColor.AMARILLO} 81%, 
                      ${CinturonColor.AMARILLO} 85%,
                      ${CinturonColor.NEGRO} 85%, 
                      ${CinturonColor.NEGRO} 88%, 
                      ${CinturonColor.AMARILLO} 88%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId === 14) {
      // IV DAN - a line and a double width line
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%,
                      ${CinturonColor.NEGRO} 78%,
                      ${CinturonColor.AMARILLO} 78%,
                      ${CinturonColor.AMARILLO} 81%, 
                      ${CinturonColor.NEGRO} 81%, 
                      ${CinturonColor.NEGRO} 85%,
                      ${CinturonColor.AMARILLO} 85%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId === 15) {
      // V DAN - single double width line
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%,
                      ${CinturonColor.NEGRO} 85%,
                      ${CinturonColor.AMARILLO} 85%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId === 16) {
      // VI DAN - a double width line and a line
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%,
                      ${CinturonColor.NEGRO} 78%,
                      ${CinturonColor.AMARILLO} 78%,
                      ${CinturonColor.AMARILLO} 85%, 
                      ${CinturonColor.NEGRO} 85%, 
                      ${CinturonColor.NEGRO} 88%,
                      ${CinturonColor.AMARILLO} 88%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%,
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else if (graduacionId >= 17) {
      // VII DAN or higher - yellow square (larger block)
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%,
                      ${CinturonColor.NEGRO} 75%,
                      ${CinturonColor.AMARILLO} 75%,
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    } else {
      // Default black belt (fallback for any other black belt ID)
      return {
        'background': `linear-gradient(to right, 
                      ${CinturonColor.NEGRO} 0%, 
                      ${CinturonColor.NEGRO} 88%, 
                      ${CinturonColor.AMARILLO} 88%, 
                      ${CinturonColor.AMARILLO} 92%, 
                      ${CinturonColor.NEGRO} 92%, 
                      ${CinturonColor.NEGRO} 100%)`
      };
    }
  }

  getGraduacionDescripcion(): string {
    if (this.isLoading) {
      return 'Cargando...';
    }
    
    return this.graduacion?.nombre || 'BLANCO/SIN EXAMENES';
  }
}